import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Download, ShieldCheck, TrendingUp, Users, Target, Lock } from "lucide-react";
import { b2mOrgPublic, b2mOrgPublicPdf, b2mOrgPublicCsv } from "@/lib/gfApi";

const INK = "#141A32";
const INK2 = "#1E2647";
const GOLD = "#D6A25A";
const TEAL = "#2FA89F";
const RED = "#D9705A";

const barCol = (s) => (s == null ? "#5B6688" : s >= 65 ? TEAL : s >= 50 ? GOLD : RED);
const rm = (v) => (v || v === 0 ? "RM" + Number(v).toLocaleString("en-MY", { maximumFractionDigits: 0 }) : "—");

function Metric({ label, value, sub, testId }) {
  return (
    <div className="rounded-2xl p-5" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid={testId}>
      <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#9AA6C2" }}>{label}</p>
      <p className="mt-1 font-heading text-3xl font-bold text-white">{value}</p>
      {sub && <p className="mt-0.5 text-xs" style={{ color: GOLD }}>{sub}</p>}
    </div>
  );
}

export default function B2MOrgReport() {
  const { code } = useParams();
  const [sp] = useSearchParams();
  const t = sp.get("t") || "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    b2mOrgPublic(code, t).then(setData).catch((e) => setErr(e?.response?.data?.detail || "Pautan tidak sah."));
  }, [code, t]);

  if (err) return (
    <div className="flex min-h-screen items-center justify-center px-8 text-center" style={{ background: INK }}>
      <div data-testid="org-error"><Lock className="mx-auto h-8 w-8" style={{ color: GOLD }} /><p className="mt-3 text-white">{err}</p></div>
    </div>
  );
  if (!data) return <div className="flex min-h-screen items-center justify-center" style={{ background: INK }}><Loader2 className="h-6 w-6 animate-spin text-[#D6A25A]" /></div>;

  const a = data.aggregate;
  const labels = data.stage_labels || {};
  const c = a.commercial;
  return (
    <div className="min-h-screen" style={{ background: INK }} data-testid="b2m-org-report-page">
      <div className="border-b" style={{ borderColor: "rgba(255,255,255,0.08)", background: "linear-gradient(135deg,#0F172A,#1E2647)" }}>
        <div className="mx-auto max-w-4xl px-6 py-8">
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>Brand2Market™ · Organisation Impact Report</p>
          <h1 className="mt-2 font-heading text-3xl font-bold text-white">{data.organisation}</h1>
          <p className="mt-1 text-sm" style={{ color: "#9AA6C2" }}>{data.programme_name} · {data.cohort_name} · n={a.n_total} peserta</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a data-testid="org-download-pdf" href={b2mOrgPublicPdf(code, t)} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold" style={{ background: GOLD, color: INK }}><Download className="h-4 w-4" /> Muat Turun PDF</a>
            <a data-testid="org-download-csv" href={b2mOrgPublicCsv(code, t)} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-white" style={{ border: "1px solid rgba(255,255,255,0.15)" }}><Download className="h-4 w-4" /> CSV</a>
          </div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mx-auto max-w-4xl space-y-6 px-6 py-8">
        <div className="grid gap-3 sm:grid-cols-3">
          <Metric label="Penyertaan" value={`n=${a.n_total}`} sub={`${a.n_pre} PRE · ${a.n_post} POST`} testId="org-metric-participation" />
          <Metric label="Peningkatan Purata" value={a.avg_improvement != null ? `+${a.avg_improvement}` : "—"} sub={a.pct_improved != null ? `${a.pct_improved}% bertambah baik (n=${a.n_improved})` : "Belum cukup data"} testId="org-metric-improvement" />
          <Metric label="Skor Purata" value={a.avg_post != null ? a.avg_post : (a.avg_pre ?? "—")} sub={`PRE ${a.avg_pre ?? "—"} → POST ${a.avg_post ?? "—"}`} testId="org-metric-score" />
        </div>

        <div className="rounded-2xl p-5" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="org-stage-movement">
          <p className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: GOLD }}><TrendingUp className="h-3.5 w-3.5" /> Pergerakan 5 Peringkat (Purata Kohort)</p>
          <div className="space-y-2.5">
            {a.stage_movement.map((s) => (
              <div key={s.key} className="flex items-center gap-2 text-xs">
                <span className="w-28 text-white">{s.label}</span>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,0.1)" }}>
                  <div className="h-full rounded-full" style={{ width: `${(s.post ?? s.pre) || 0}%`, background: barCol(s.post ?? s.pre) }} />
                </div>
                <span className="w-24 text-right text-white">{s.pre ?? "—"}{s.post != null ? ` → ${s.post}` : ""}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl p-5" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="org-commercial">
            <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: GOLD }}><Target className="h-3.5 w-3.5" /> Hasil Komersial</p>
            <p className="text-sm text-white">Jualan bulanan (terkini): <b>{rm(c.total_monthly_sales)}</b> <span style={{ color: "#9AA6C2" }}>(n={c.n_sales})</span></p>
            <p className="mt-1 text-sm text-white">Jualan eksport: <b>{rm(c.total_export_sales)}</b> <span style={{ color: "#9AA6C2" }}>(n={c.n_export})</span></p>
            <p className="mt-1 text-sm text-white">Pertanyaan eksport: <b>{c.n_export_enquiries}</b> / {a.n_total}</p>
          </div>
          <div className="rounded-2xl p-5" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="org-verified">
            <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: GOLD }}><ShieldCheck className="h-3.5 w-3.5" /> Bukti Disahkan Konsultan</p>
            <p className="text-sm text-white">Sijil disahkan: <b>{a.certs_verified}</b></p>
            <p className="mt-1 text-sm text-white">Peserta dengan sijil disahkan: <b>{a.parts_with_verified_cert}</b></p>
            <p className="mt-1 text-sm text-white">Bukti komersial disahkan: <b>{a.evidence_verified}</b></p>
          </div>
        </div>

        <div className="rounded-2xl p-5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="org-privacy">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: "#9AA6C2" }}><Lock className="h-3.5 w-3.5" /> Data & Privasi</p>
          <p className="mt-2 text-xs" style={{ color: "#9AA6C2" }}>Laporan agregat ini untuk organisasi penaja sahaja. Ia tidak mengandungi nota peribadi konsultan atau dokumen/fail peserta. Semua metrik menggunakan denominator jujur (n=XX); data komersial adalah lapor-kendiri kecuali ditanda sebagai disahkan.</p>
        </div>
        <p className="pb-8 text-center text-[11px]" style={{ color: "#5B6688" }}>Data demonstrasi untuk tujuan pembentangan sahaja apabila kohort ditanda DEMO.</p>
      </motion.div>
    </div>
  );
}
